import { Stack } from '@mui/material';

import { theme, Typography } from '@/design-system';
import { ShareholderList, ShareStructureTemplate } from '@/features/share-structure';
import { useEntitySlice } from '@/stores/selectors';
import { StepForms } from '@/stores/slices/entity-slice';
import { onlyNumbersWithoutDigits } from '@/utils/numbers/helpers';

import { selectClassOptions } from '../common';
import { SectionHeading, StepSection } from '../styled';
import { ENTITY_FIELD_ACCESS, useEntityFieldAccess } from '../use-entity-field-access';
import { useEntityReviewMode } from '../use-entity-review-mode';
import { getStepFieldErrors } from '../validate-step';
import { useWizardTitleSlot } from '../wizard-title-slot';
import { UpdatedBadge } from './wizard-status-badges';

const SHARE_STRUCTURE_FIELD = 'shareStructure';

const shareClassPath = (classIdx: number, field: string) =>
  `${SHARE_STRUCTURE_FIELD}.shareClasses[${classIdx}].${field}`;

const shareholderPath = (classIdx: number, shareholderIdx: number) =>
  `${SHARE_STRUCTURE_FIELD}.shareClasses[${classIdx}].shareholders[${shareholderIdx}]`;

type EntityShareClass = StepForms['shareStructure']['shareClasses'][number];
type EntityShareholder = NonNullable<EntityShareClass['shareholders']>[number];

const calcTotalNumberOfShares = (classes: EntityShareClass[]) => {
  const nums = classes
    .map((c) => c.numberOfShares)
    .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));

  return nums.length ? nums.reduce((sum, v) => sum + v, 0) : null;
};

export const DefineShareStructure = () => {
  const { stepForms, updateStepForm, currentStep, validatedSteps } = useEntitySlice();
  const { forms, isChangedUnder, highlightIf, highlightColor, isChanged, isReviewMode } =
    useEntityReviewMode();
  const isShareStructureChanged = isChangedUnder(SHARE_STRUCTURE_FIELD);
  const { shareClasses } = forms.shareStructure;
  const { accessOf, deniedReasonFor } = useEntityFieldAccess();
  const isDenied = accessOf(SHARE_STRUCTURE_FIELD) === ENTITY_FIELD_ACCESS.denied;
  const deniedReason = deniedReasonFor(SHARE_STRUCTURE_FIELD);

  const isStepValidated = validatedSteps.includes(currentStep);
  const fieldErrors = getStepFieldErrors(3, stepForms);
  const titleSlot = useWizardTitleSlot();

  const setShareClasses = (nextClasses: EntityShareClass[], recalcTotal = false) => {
    updateStepForm({
      shareStructure: {
        ...stepForms.shareStructure,
        shareClasses: nextClasses,
        totalNumberOfShares: recalcTotal
          ? calcTotalNumberOfShares(nextClasses)
          : stepForms.shareStructure.totalNumberOfShares,
      },
    });
  };

  const handleTotalSharesChange = (classIdx: number, raw: string) => {
    const digits = onlyNumbersWithoutDigits(raw);
    const next = digits === '' ? null : Number(digits);

    const nextClasses = shareClasses.map((c, i) =>
      i !== classIdx ? c : { ...c, numberOfShares: next },
    );

    setShareClasses(nextClasses, true);
  };

  const handleClassChange = (classIdx: number, newValue: string) => {
    const nextClasses = shareClasses.map((c, i) =>
      i !== classIdx ? c : { ...c, class: newValue },
    );
    setShareClasses(nextClasses);
  };

  const addShareClass = () => {
    const newShareClass: EntityShareClass = {
      class: null,
      numberOfShares: null,
      shareholders: [{ type: null, email: null, fullName: null, numberOfShares: null }],
    };

    const nextClasses = [...shareClasses, newShareClass];
    setShareClasses(nextClasses, true);
  };

  const removeShareClass = (index: number) => {
    const nextClasses = shareClasses.filter((_, i) => i !== index);
    setShareClasses(nextClasses, true);
  };

  const updateShareholder = (
    classIdx: number,
    shareholderIdx: number,
    field: keyof EntityShareholder,
    value: string,
  ) => {
    const nextValue =
      field === 'numberOfShares'
        ? (() => {
            const digits = onlyNumbersWithoutDigits(value);
            return digits === '' ? null : Number(digits);
          })()
        : value
          ? value
          : null;

    const nextClasses = shareClasses.map((cls, i) => {
      if (i !== classIdx) return cls;

      const shareholders = [...(cls.shareholders ?? [])];
      shareholders[shareholderIdx] = {
        ...shareholders[shareholderIdx],
        [field]: nextValue,
      };

      return { ...cls, shareholders };
    });

    setShareClasses(nextClasses);
  };

  const addShareholder = (classIdx: number) => {
    const nextClasses = shareClasses.map((cls, i) => {
      if (i !== classIdx) return cls;

      return {
        ...cls,
        shareholders: [
          ...(cls.shareholders ?? []),
          { type: null, email: null, fullName: null, numberOfShares: null },
        ],
      };
    });

    setShareClasses(nextClasses);
  };

  const removeShareholder = (classIdx: number, shareholderIdx: number) => {
    const nextClasses = shareClasses.map((cls, i) => {
      if (i !== classIdx) return cls;

      return {
        ...cls,
        shareholders: (cls.shareholders ?? []).filter((_, idx) => idx !== shareholderIdx),
      };
    });

    setShareClasses(nextClasses);
  };

  return (
    <StepSection $changed={highlightIf(isShareStructureChanged)}>
      {isShareStructureChanged && (
        <SectionHeading>
          <UpdatedBadge />
        </SectionHeading>
      )}

      {deniedReason && (
        <Typography variant="body3" color={theme.colors.text.secondary}>
          {deniedReason}
        </Typography>
      )}

      <Stack flexDirection="column" gap={`${theme.spacing[16]}px`}>
        <ShareStructureTemplate
          shareClasses={shareClasses}
          selectClassOptions={selectClassOptions}
          disabled={isDenied || isReviewMode}
          fieldErrors={fieldErrors}
          isStepValidated={isStepValidated}
          onClassChange={handleClassChange}
          onTotalSharesChange={handleTotalSharesChange}
          onRemoveShareClass={removeShareClass}
          onAddShareClass={addShareClass}
          addButtonSlot={titleSlot}
          canAddMore={shareClasses.length < selectClassOptions.length}
          changedColor={highlightColor}
          getIsClassChanged={(index) => isChanged(shareClassPath(index, 'class'))}
          getIsTotalSharesChanged={(index) => isChanged(shareClassPath(index, 'numberOfShares'))}
          renderShareholders={(el, idx) => (
            <ShareholderList
              shareClass={el}
              classIdx={idx}
              disabled={isDenied}
              isStepValidated={isStepValidated}
              fieldErrors={fieldErrors}
              onUpdateShareholder={updateShareholder}
              onAddShareholder={addShareholder}
              onRemoveShareholder={removeShareholder}
              changedColor={highlightColor}
              isChanged={isChanged}
              getShareholderPath={shareholderPath}
            />
          )}
        />
      </Stack>
    </StepSection>
  );
};
